import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC, useCallback } from 'react';
import ArrowUpSvg from '../../../images/arrowUp.svg';
import { decreaseFormPage, setFormPage } from '../../../reactive/actions';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import Colors from '../../../utils/Colors';
import { FORM_PAGES } from '../../Form/types';
import { FORM_CHOICE_TYPES } from '../../FormChoice/types';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { Accessibility, Button, Buttons, FormContainerItems, FormContainerWrapper, FormTitle, PreviousButton, Submit, Title } from './styled';
export type PreviousPage = {
  page?: FORM_PAGES;
  animation?: 'fade' | 'fade-out';
  onPrevious?: () => void;
  afterTransitionToPrev?: () => void;
};
interface FormContainerProps {
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  title: string;
  noNextButton?: boolean;
  noPreviousButton?: boolean;
  nextButtonText?: string;
  accessibility?: boolean;
  children: React.ReactNode;
  loadingData?: boolean;
  nextButtonSiblings?: React.ReactNode;
  loading?: boolean;
  previousPage?: PreviousPage;
  error?: string | boolean;
  aboveTitleElement?: React.ReactNode;
  extraButtonsStyles?: React.CSSProperties;
  stickyNextButton?: boolean;
  centerTitle?: boolean;
  buttonMargin?: string;
}

const FormContainer: FC<FormContainerProps> = ({
  onSubmit = () => {},
  title = '',
  noNextButton = false,
  noPreviousButton = false,
  nextButtonText,
  accessibility = true,
  loadingData = false,
  nextButtonSiblings,
  children,
  loading = false,
  previousPage,
  error,
  aboveTitleElement,
  extraButtonsStyles,
  stickyNextButton = false,
  centerTitle = false,
  buttonMargin
}) => {
  const { t } = useTranslation();
  const textForNextButton = nextButtonText || t('next');

  const renderNextButton = useCallback(() => {
    return (
      <Buttons style={extraButtonsStyles}>
        {noNextButton && loading && <LoadingSpinner color={Colors.black} />}

        {!noNextButton && (
          <Submit buttonMargin={buttonMargin}>
            <Button type="submit" disabled={!!error} autoResize>
              {loading ? <LoadingSpinner color={Colors.primaryGray} /> : <span>{textForNextButton}</span>}
            </Button>
            {accessibility && (
              <Accessibility>
                {t('orPress')} <span> Enter</span>
              </Accessibility>
            )}
          </Submit>
        )}
        {nextButtonSiblings}
      </Buttons>
    );
  }, [noNextButton, loading, error, nextButtonText, accessibility, nextButtonSiblings, extraButtonsStyles, textForNextButton]);

  return (
    <FormContainerWrapper onSubmit={onSubmit} scrollable={!stickyNextButton} alignItems={noNextButton && !FORM_CHOICE_TYPES.RECURRENCE ? 'flex-start' : 'center'}>
      <FormContainerItems scrollable={stickyNextButton}>
        {!noPreviousButton && (
          <PreviousButton
            type="button"
            onClick={() => {
              trackEvent('click', {
                name: AnalyticsEvents.PREVIOUS_BUTTON
              });
              if (!previousPage) {
                decreaseFormPage();
                return;
              }

              if (previousPage?.onPrevious) {
                previousPage.onPrevious();
                return;
              }

              setFormPage(previousPage.page || FORM_PAGES.PRODUCTS, previousPage?.animation || 'fade-out');

              if (previousPage?.afterTransitionToPrev) {
                previousPage.afterTransitionToPrev?.();
              }
            }}
            disabled={loading}
          >
            <ArrowUpSvg />
          </PreviousButton>
        )}
        {aboveTitleElement}
        <FormTitle>
          <Title center={centerTitle}>{title}</Title>
          {loadingData && <LoadingSpinner />}
        </FormTitle>
        {children}
        {!stickyNextButton && renderNextButton()}
      </FormContainerItems>
      {stickyNextButton && renderNextButton()}
    </FormContainerWrapper>
  );
};

export default FormContainer;
